import { createContext, useContext } from 'react';
import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { KakaoAddressDocument } from '@/common/types';
import type { CouponDraft } from '@/pages/Seminar/components/form/InstructorCouponDialog';
import type { CouponPendingUpdate } from '@/pages/Seminar/components/form/InstructorCouponSection';
import type {
  CouponBasis,
  DraftStation,
  MetaGroup,
  SeminarFormStep,
  SeminarFormValues,
} from '@/pages/Seminar/components/form/seminarFormModel';
import type {
  CancelWindowSource,
  SeminarFormatValue,
  SeminarScheduleValue,
  SeminarTypeValue,
} from '@/pages/Seminar/components/form/seminarFormUtils';

/** 수정 화면 세미나 관리(마감·재모집·폐강·판매 종료)용 현재 상태 */
export type SeminarManageStatus = CancelWindowSource & {
  isRecruitmentClosed?: boolean;
  recruitmentEndDateTime?: string;
  /** 상시 동영상 판매 종료 */
  salesClosed?: boolean;
};

/** 한 단계의 첫 빠진 항목 — 누르면 그 칸으로 */
export interface StepProblem {
  field: string;
  message: string;
}

/**
 * 단계 카드(폰 5단계·PC 카드 5장)가 같이 쓰는 폼 상태와 동작.
 * 규칙·검증·저장은 SeminarForm 이 갖고, 단계 카드는 그리기와 입력 전달만 한다.
 */
export interface SeminarFormContextValue {
  control: Control<SeminarFormValues>;
  errors: FieldErrors<SeminarFormValues>;
  values: SeminarFormValues;
  setValue: UseFormSetValue<SeminarFormValues>;
  watch: UseFormWatch<SeminarFormValues>;

  isEditMode: boolean;
  seminarId?: string;
  paymentEnabled: boolean;
  /** 화면에서 고른 유형(결제 기능이 꺼져 있으면 유형 선택이 없다) */
  seminarType: SeminarTypeValue;
  originalSeminarType: SeminarTypeValue;
  career: boolean;
  onTypeChange: (type: SeminarTypeValue) => void;
  categoryGroups: MetaGroup[];

  format: SeminarFormatValue;
  schedule: SeminarScheduleValue;
  openSale: boolean;
  isOnlineFormat: boolean;
  showAddress: boolean;
  lockPriceSchedule: boolean;
  today: string;
  onFormatChange: (format: SeminarFormatValue) => void;
  onScheduleChange: (schedule: SeminarScheduleValue) => void;
  onAddressChange: (address: KakaoAddressDocument) => void;
  /** 고른 지하철역(이름 포함) — 폼 값 stations 는 id 만 */
  stationObjects: DraftStation[];
  onStationsChange: (stations: DraftStation[]) => void;

  refundGuide: string;
  showMidwayCancelNotice: boolean;
  midwayNoticeText: string;
  vodRefundNotice: string | null;
  /** 목록 카드 일정 줄 미리보기 */
  scheduleLabel: string | null;
  openSaleSaved: boolean;

  couponDrafts: CouponDraft[];
  onCouponDraftsChange: (next: CouponDraft[]) => void;
  couponDraftNotice: string;
  /** 쿠폰 창·검사·미리보기 기준 — 폼에 지금 입력된 참가비·모집 마감(한 값) */
  couponBasis: CouponBasis;
  /** 수정 화면: 기준이 저장된 값과 달라 쿠폰을 바로 저장하지 않고 담아 둔다(세미나 저장 때 반영) */
  couponDeferred: boolean;
  /** 수정 화면: 저장 전 담아 둔 기존 쿠폰 고치기 */
  couponUpdates: CouponPendingUpdate[];
  onCouponUpdatesChange: (next: CouponPendingUpdate[]) => void;
  /** 바뀌면 쿠폰 목록을 다시 읽는다 */
  couponReloadKey: number;

  /** 설명 에디터를 새로 그릴 때(임시 저장 이어 쓰기) 바꾸는 키 */
  editorKey: number;
  showAlert: (message: string, callback?: () => void, action?: { label: string; onClick: () => void }) => void;

  directDealAgreed: boolean;
  setDirectDealAgreed: (agreed: boolean) => void;

  /** 단계별 첫 빠진 항목(없으면 그 단계는 다 채움) — 확인 단계·PC 빠진 항목 목록 */
  stepProblems: Partial<Record<SeminarFormStep, StepProblem>>;
  /** 단계별 빠진 칸 전부(화면 순서) — PC 오른쪽 단계 목록의 "빠진 항목 짧게" */
  stepMissing: Partial<Record<SeminarFormStep, string[]>>;
  /** 지금 단계·지나온 단계 — PC 오른쪽 단계 목록 강조 */
  currentStep: SeminarFormStep;
  /** 필수 항목·운영 원칙 동의까지 다 됐는지 — 등록 버튼 활성 */
  readyToSubmit: boolean;
  /** 커리어로 새로 등록·전환 — 운영 원칙 동의 체크가 필요하다 */
  needsDirectDealAgree: boolean;
  /** 지금 값 기준 칸별 오류(표시용) */
  liveErrors: Partial<Record<keyof SeminarFormValues, string>>;
  /** 그 칸이 있는 단계로 옮기고 칸에 포커스 */
  goToField: (field: string) => void;
  goToStep: (step: SeminarFormStep) => void;

  /** 수정 화면 세미나 관리 */
  manage: {
    status: SeminarManageStatus | null;
    showClose: boolean;
    showReopen: boolean;
    showCancel: boolean;
    managing: boolean;
    togglingSales: boolean;
    onClose: () => void;
    onReopen: () => void;
    onCancel: () => void;
    onToggleSales: () => void;
  };
}

export const SeminarFormContext = createContext<SeminarFormContextValue | null>(null);

export const useSeminarForm = (): SeminarFormContextValue => {
  const value = useContext(SeminarFormContext);
  if (!value) throw new Error('SeminarFormContext 밖에서 단계 카드를 그렸어요.');
  return value;
};
